import React, { useState } from "react";
import { StyleSheet, View, type StyleProp, type ViewStyle, type TextStyle } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IFilteredEntityResultSet } from "@/services/memory-filter-service";
import {
  exportVisualiserToPdf,
  exportVisualiserToImage,
} from "@/services/memory-export-service";

export interface IMemoryExporterProps {
  /** The entity result set (memories, media, contacts, groups) to export */
  data: IFilteredEntityResultSet;
  /** Optional SVG markup string of the D3 graph layout for PDF embedding */
  svgHtml?: string;
  /** Optional HTML element ID of the SVG element for image capture (defaults to "memories-visualiser-svg") */
  svgElementId?: string;
  /** Custom PDF export handler override */
  onExportPdf?: () => Promise<void> | void;
  /** Custom Image export handler override */
  onExportImage?: () => Promise<void> | void;
  /** Whether to show the PDF export button (default: true) */
  showPdf?: boolean;
  /** Whether to show the Image export button (default: true) */
  showImage?: boolean;
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Optional button style override */
  buttonStyle?: StyleProp<ViewStyle>;
  /** Optional button text style override */
  buttonTextStyle?: StyleProp<TextStyle>;
}

/**
 * Standardized Archival Heirloom Memory Exporter component.
 * Provides dedicated PDF and Image export triggers for memory archives,
 * relationship graphs, and filtered entity result sets.
 */
export function MemoryExporter({
  data,
  svgHtml,
  svgElementId = "memories-visualiser-svg",
  onExportPdf,
  onExportImage,
  showPdf = true,
  showImage = true,
  style,
  buttonStyle,
  buttonTextStyle,
}: IMemoryExporterProps): React.JSX.Element {
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingImage, setIsExportingImage] = useState(false);
  const tintColor = useThemeColor({}, "tint");

  const handleExportPdfPress = async (): Promise<void> => {
    if (isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      if (onExportPdf) {
        await onExportPdf();
      } else {
        await exportVisualiserToPdf(data, svgHtml);
      }
    } catch (err: any) {
      console.error("[MemoryExporter] Export to PDF failed:", err);
      CustomAlert.alert("Export Failed", err?.message || "Could not generate PDF export.");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportImagePress = async (): Promise<void> => {
    if (isExportingImage) return;
    setIsExportingImage(true);
    try {
      if (onExportImage) {
        await onExportImage();
      } else {
        await exportVisualiserToImage(svgElementId);
      }
    } catch (err: any) {
      console.warn("[MemoryExporter] Export to Image notice:", err?.message);
      CustomAlert.alert(
        "Export Image",
        "Image export captures the Visualiser graph canvas. Switch to the Visualiser view to export as image, or use PDF to export memory chronicles."
      );
    } finally {
      setIsExportingImage(false);
    }
  };

  return (
    <View style={[styles.container, style]}>
      {showPdf && (
        <ThemedButton
          title={isExportingPdf ? "..." : "PDF"}
          icon={<Ionicons name="document-text-outline" size={13} color={tintColor} />}
          variant="secondary"
          onPress={handleExportPdfPress}
          disabled={isExportingPdf}
          style={[styles.exportBtn, buttonStyle]}
          textStyle={[styles.exportBtnText, buttonTextStyle]}
          accessibilityLabel="Export memories to PDF"
        />
      )}

      {showImage && (
        <ThemedButton
          title={isExportingImage ? "..." : "Image"}
          icon={<Ionicons name="image-outline" size={13} color={tintColor} />}
          variant="secondary"
          onPress={handleExportImagePress}
          disabled={isExportingImage}
          style={[styles.exportBtn, buttonStyle]}
          textStyle={[styles.exportBtnText, buttonTextStyle]}
          accessibilityLabel="Export memories to Image"
        />
      )}
    </View>
  );
}

export default MemoryExporter;

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  exportBtn: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 0,
    minHeight: 28,
  },
  exportBtnText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
